import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getDoc, deleteDoc, doc, updateDoc, onSnapshot } from "firebase/firestore";
import { sendEmailVerification } from "firebase/auth";
import _ from "lodash";
import slug from "slug";
import arrayMove from "lodash-move";
import { ExternalLink, Trash2, Eye, EyeOff, RefreshCw } from "lucide-react";

import { auth, db, isAdmin } from "../../firebase.js";
import { geocodeAddress } from "../../venueTools.js";
import i18n from "../../i18n.js";
import Gallery from "./components/Gallery.js";
import VenueSelect from "../../components/VenueSelect.js";
import Button from "../../components/Button.js";
import Message from "../../components/Message.js";
import Tips from "./components/Tips.js";
import DescriptionGenerator from "./components/DescriptionGenerator.js";
import PhoneUploadCard from "../../components/PhoneUploadCard.js";

const errorMessages = {
  "auth/too-many-requests":
    "Er is al een verificatie email verstuurd naar uw emailadres. Indien u niets heeft ontvangen (check ook uw spam); probeer het dan over 5 minuten nog eens door op de onderstaande knop te drukken.",
};

const initialVenue = {
  name: "",
  address: { street: "", postalCode: "", province: "", houseNumber: "", city: "" },
  telephone: "",
  description: { nl: "" },
  website: "",
  email: "",
  photos: [],
  accessibility: [],
  types: [],
  features: [],
  maxCapacity: 0,
  minCapacity: 0,
  isB2BOnly: false,
  exclusiveHire: false,
  exclusiveHirePrice: "",
  pricePerPerson: "",
  exclusiveHireMinPersons: "",
  whatsappOptIn: false,
  eventTypes: [],
};

function Modal({ open, onClose, children }) {
  useEffect(() => {
    if (!open) return;
    const handleEsc = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div role="dialog" aria-modal="true" className="relative bg-bg-surface rounded-lg shadow-xl max-w-modal w-full animate-slide-up p-6">
        {children}
      </div>
    </div>
  );
}

function FeatureChip({ label, checked, onChange, name }) {
  return (
    <label
      className={[
        "flex items-center gap-2.5 px-3 py-2.5 rounded-sm cursor-pointer border transition-all duration-fast text-sm",
        checked
          ? "bg-accent-subtle border-accent/30 text-accent font-medium"
          : "bg-bg-surface border-border text-text-secondary hover:border-border-hover hover:bg-bg-sunken",
      ].join(" ")}
    >
      <input type="checkbox" name={name} checked={checked} onChange={onChange} className="sr-only" />
      <span
        className={[
          "w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 transition-colors duration-fast",
          checked ? "bg-accent border-accent" : "border-border-hover bg-bg-surface",
        ].join(" ")}
      >
        {checked && (
          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      {label}
    </label>
  );
}

function Card({ children, title, className = "" }) {
  return (
    <div className={`card p-6 ${className}`}>
      {title && <h3 className="text-lg font-heading font-bold mb-5">{title}</h3>}
      {children}
    </div>
  );
}

function FormField({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label-base">
        {label}
        {required && <span className="text-error ml-0.5">*</span>}
      </span>
      {children}
    </label>
  );
}

function Venue() {
  const [pending, setPending] = useState(false);
  const [saved, setSaved] = useState(false);
  const [pendingRemove, setPendingRemove] = useState(false);
  const [pendingPublishToggle, setPendingPublishToggle] = useState(false);
  const [venue, setVenue] = useState(initialVenue);
  const [error, setError] = useState("");
  const [errorVerificationEmail, setErrorVerificationEmail] = useState("");
  const [emailSend, setEmailSend] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(() => window.innerWidth >= 1280);
  const [previewKey, setPreviewKey] = useState(0);
  const iframeRef = useRef(null);
  // Foto's die bij het laden op de locatie stonden. Zo kunnen we bij het opslaan
  // foto's die intussen via de telefoon-uploadlink zijn toegevoegd behouden,
  // zonder handmatig verwijderde foto's opnieuw toe te voegen.
  const initialPhotosRef = useRef([]);
  // Lokaal verwijderde foto's, zodat de live-listener ze niet opnieuw toevoegt.
  const removedPhotosRef = useRef(new Set());

  const params = useParams();
  const navigate = useNavigate();

  async function onSubmit(event) {
    event && event.preventDefault();
    setError("");
    setPending(true);
    setSaved(false);

    const data = { ..._.omit(venue, ["objectID"]) };
    if (data.name) data.slug = slug(data.name, { lower: true });

    // Voeg foto's samen die sinds het laden via de telefoon-uploadlink zijn
    // toegevoegd, zodat we ze niet per ongeluk overschrijven.
    try {
      const fresh = await getDoc(doc(db, "locations", venue.objectID));
      const serverPhotos = (fresh.exists() && fresh.data().photos) || [];
      const initial = initialPhotosRef.current || [];
      const localPhotos = data.photos || [];
      const phoneAdded = serverPhotos.filter((id) => !initial.includes(id) && !localPhotos.includes(id));
      if (phoneAdded.length) data.photos = [...localPhotos, ...phoneAdded];
    } catch (_e) {
      // Bij twijfel gewoon doorgaan met de lokale foto's.
    }

    if (data.address && data.address.street && data.address.houseNumber && data.address.postalCode && data.address.city) {
      let loc;
      try {
        loc = await geocodeAddress(`${data.address.street} ${data.address.houseNumber}, ${data.address.postalCode} ${data.address.city}`);
      } catch (err) {
        setError("Controleer de adres gegevens van uw locatie.");
        console.log(err);
        setPending(false);
        return;
      }

      if (loc && loc.lng && loc.lat) {
        data._geoloc = {
          lng: parseFloat(loc.lng.toPrecision(6)),
          lat: parseFloat(loc.lat.toPrecision(5)),
        };
      }
    }

    try {
      await updateDoc(doc(db, "locations", venue.objectID), data);
      if (data.photos) {
        setVenue((v) => ({ ...v, photos: data.photos }));
        initialPhotosRef.current = data.photos;
      }
      setSaved(true);
    } catch (err) {
      console.log(err);
      setError(err.message);
    }
    setPending(false);
  }

  async function onRemove(event) {
    event && event.preventDefault();
    if (!window.confirm("Weet u zeker dat u deze locatie wilt verwijderen?")) return;
    setPendingRemove(true);
    try {
      await deleteDoc(doc(db, "locations", venue.objectID));
      window.scrollTo(0, 0);
      navigate(`/venues`);
    } catch (err) {
      console.log(err);
      setError(err.message);
    }
    setPendingRemove(false);
  }

  async function onTogglePublished() {
    if (!venue.objectID) return;
    const next = !venue.published;
    setPendingPublishToggle(true);
    setError("");
    try {
      await updateDoc(doc(db, "locations", venue.objectID), { published: next });
      setVenue((v) => ({ ...v, published: next }));
    } catch (err) {
      console.log(err);
      setError(err.message);
    }
    setPendingPublishToggle(false);
  }

  async function onVerifyEmail() {
    setEmailSend(false);
    setErrorVerificationEmail("");
    try {
      await sendEmailVerification(auth.currentUser);
      setEmailSend(true);
    } catch (err) {
      console.log(err.message);
      setErrorVerificationEmail(errorMessages[err.code] || err.message);
    }
  }

  useEffect(() => {
    async function getData() {
      setError("");
      if (!params.venueId) {
        setVenue(initialVenue);
        return;
      }
      try {
        const locDoc = await getDoc(doc(db, "locations", params.venueId));
        if (!locDoc.exists()) {
          setError(`De locatie met id '${params.venueId}' bestaat niet (meer).`);
          setVenue(initialVenue);
          return;
        }
        const loaded = { ..._.cloneDeep(initialVenue), objectID: locDoc.id, ...locDoc.data() };
        initialPhotosRef.current = loaded.photos || [];
        setVenue(loaded);
      } catch (err) {
        console.log(err);
        setError(err.message);
      }
    }
    getData();
  }, [params.venueId]);

  // Live: foto's die via de telefoon-uploadlink binnenkomen verschijnen meteen in
  // de galerij, zonder foto's te resurrecten die de eigenaar zelf verwijderde.
  useEffect(() => {
    if (!params.venueId) return undefined;
    const unsub = onSnapshot(doc(db, "locations", params.venueId), (snap) => {
      if (!snap.exists()) return;
      const serverPhotos = snap.data().photos || [];
      setVenue((v) => {
        if (!v.objectID) return v;
        const additions = serverPhotos.filter(
          (id) => !v.photos.includes(id) && !initialPhotosRef.current.includes(id) && !removedPhotosRef.current.has(id),
        );
        return additions.length ? { ...v, photos: [...v.photos, ...additions] } : v;
      });
    }, (err) => console.error("Foto-listener:", err));
    return () => unsub();
  }, [params.venueId]);

  useEffect(() => {
    if (!previewOpen || !iframeRef.current) return;
    iframeRef.current.contentWindow?.postMessage({ type: "venue-update", venue }, "https://feestjegeven.nl");
  }, [venue, previewOpen]);

  if (!venue.objectID) {
    return (
      <div>
        <VenueSelect
          activeVenueId={params.venueId}
          onChange={(e, doc) => {
            if (!doc) {
              navigate(`/venues`);
              return;
            }
            navigate(`/venues/${doc.objectID}`);
          }}
          showNewButton={true}
        />
        {error && <Message text={error} />}
      </div>
    );
  }

  return (
    <div className={`transition-[padding] duration-slow ease-snappy ${previewOpen && venue.objectID ? "xl:pr-[544px]" : ""}`}>
      <VenueSelect
        activeVenueId={params.venueId}
        onChange={(e, doc) => {
          if (!doc) {
            navigate(`/venues`);
            return;
          }
          navigate(`/venues/${doc.objectID}`);
        }}
        showNewButton={true}
      />

      <Modal open={saved} onClose={() => setSaved(false)}>
        <h3 className="text-xl font-heading font-bold mb-2">Gelukt!</h3>
        <p className="text-text-secondary mb-5">Uw wijzigingen zijn opgeslagen.</p>
        <Button label="Sluiten" onClick={() => setSaved(false)} />
      </Modal>

      <Modal open={emailSend} onClose={() => setEmailSend(false)}>
        <h3 className="text-xl font-heading font-bold mb-2">Email verzonden</h3>
        <p className="text-text-secondary mb-5">
          Er is een email verzonden naar uw emailadres. Klik op de link in de email om uw emailadres te bevestigen.
        </p>
        <Button label="Sluiten" onClick={() => setEmailSend(false)} />
      </Modal>

      <div className="space-y-6">
        {isAdmin() && (
          <div className="card border-accent/30 bg-accent-subtle p-5">
            <div className="flex items-start sm:items-center justify-between gap-4 flex-col sm:flex-row">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-accent text-text-inverse text-[10px] font-semibold tracking-wider uppercase">
                    Admin
                  </span>
                  <h3 className="text-base font-heading font-bold">Publicatiestatus</h3>
                </div>
                <p className="text-sm text-text-secondary">
                  Deze locatie is momenteel{" "}
                  <strong className={venue.published ? "text-success" : "text-warning"}>
                    {venue.published ? "gepubliceerd" : "niet gepubliceerd"}
                  </strong>
                  .
                </p>
              </div>
              <Button
                variant={venue.published ? "secondary" : "primary"}
                label={venue.published ? "Depubliceren" : "Publiceren"}
                onClick={onTogglePublished}
                pending={pendingPublishToggle}
                loadingLabel="Een momentje.."
                icon={venue.published ? EyeOff : Eye}
              />
            </div>
          </div>
        )}

        {auth?.currentUser && !auth.currentUser.emailVerified && (
          <Message variant="warning">
            <div>
              <span className="font-medium block mb-1">Bevestig uw emailadres</span>
              <p className="text-sm opacity-80">Voordat wij uw locatie online zetten is het nodig om uw emailadres te bevestigen.</p>
              {errorVerificationEmail && <p className="text-sm mt-2">{errorVerificationEmail}</p>}
              <button onClick={() => onVerifyEmail()} className="text-sm font-medium underline underline-offset-2 mt-2 hover:no-underline">
                Deze email opnieuw verzenden
              </button>
            </div>
          </Message>
        )}

        {venue.objectID && !pending && !venue.published && (
          <Message variant="info">
            <div>
              <span className="font-medium block mb-1">Binnenkort online</span>
              <p className="text-sm opacity-80">Uw locatie wordt na controle binnen 24 uur online gezet.</p>
            </div>
          </Message>
        )}

        {venue.objectID && venue.published && (
          <a
            target="_blank"
            rel="noopener noreferrer"
            href={`https://feestjegeven.nl/l/${venue.slug}/${venue.objectID}`}
            className="inline-flex items-center gap-2 text-sm text-accent font-medium hover:underline underline-offset-2"
          >
            <ExternalLink size={14} /> Bekijk online
          </a>
        )}

        <form method="POST" onSubmit={onSubmit} className="space-y-6">
          <Card title="Algemeen">
            <div className="space-y-5">
              <FormField label="Naam" required>
                <input
                  type="text"
                  name="name"
                  placeholder="Naam van de locatie"
                  required
                  value={venue.name}
                  onChange={(e) => setVenue({ ...venue, name: e.target.value })}
                  className="input-base"
                />
              </FormField>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="isB2BOnly"
                  checked={venue.isB2BOnly}
                  onChange={(e) => setVenue({ ...venue, isB2BOnly: e.target.checked })}
                  className="sr-only peer"
                />
                <span className="w-4 h-4 rounded-sm border border-border-hover bg-bg-surface peer-checked:bg-accent peer-checked:border-accent flex items-center justify-center transition-colors duration-fast">
                  {venue.isB2BOnly && (
                    <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                      <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span className="text-sm text-text-secondary">Onze locatie is alleen te boeken door bedrijven</span>
              </label>
            </div>
          </Card>

          <Card title="Adres">
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-4">
                <FormField label="Straat" required>
                  <input
                    type="text"
                    name="address.street"
                    required
                    value={venue.address.street}
                    onChange={(e) => setVenue({ ...venue, address: { ...venue.address, street: e.target.value } })}
                    className="input-base"
                  />
                </FormField>
                <FormField label="Nr." required>
                  <input
                    type="text"
                    name="address.houseNumber"
                    required
                    value={venue.address.houseNumber}
                    onChange={(e) => setVenue({ ...venue, address: { ...venue.address, houseNumber: e.target.value } })}
                    className="input-base"
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-[140px_1fr] gap-4">
                <FormField label="Postcode" required>
                  <input
                    type="text"
                    name="address.postalCode"
                    required
                    value={venue.address.postalCode}
                    onChange={(e) => setVenue({ ...venue, address: { ...venue.address, postalCode: e.target.value } })}
                    className="input-base"
                  />
                </FormField>
                <FormField label="Plaats" required>
                  <input
                    type="text"
                    name="address.city"
                    required
                    value={venue.address.city}
                    onChange={(e) => setVenue({ ...venue, address: { ...venue.address, city: e.target.value } })}
                    className="input-base"
                  />
                </FormField>
              </div>

              <FormField label="Provincie" required>
                <select
                  name="address.province"
                  required
                  value={venue.address.province}
                  onChange={(e) => setVenue({ ...venue, address: { ...venue.address, province: e.target.value } })}
                  className="input-base appearance-none cursor-pointer"
                >
                  <option value="">--- Kies provincie ---</option>
                  {[
                    "Drenthe",
                    "Flevoland",
                    "Friesland",
                    "Gelderland",
                    "Groningen",
                    "Limburg",
                    "Noord-Brabant",
                    "Noord-Holland",
                    "Overijssel",
                    "Utrecht",
                    "Zeeland",
                    "Zuid-Holland",
                  ].map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
              </FormField>
            </div>
          </Card>

          <Card title="Contact">
            <div className="space-y-5">
              <div>
                <FormField label="Website" required>
                  <input
                    type="text"
                    name="website"
                    required
                    value={venue.website || ""}
                    onChange={(e) => setVenue({ ...venue, website: e.target.value })}
                    className="input-base"
                  />
                </FormField>
                {!venue.premium && (
                  <p className="text-xs text-text-tertiary mt-1.5 leading-relaxed">
                    Upgrade naar een{" "}
                    <a href="/membership" className="text-accent font-medium hover:underline">
                      Premium lidmaatschap
                    </a>{" "}
                    om een link naar uw website te tonen.
                  </p>
                )}
              </div>

              <FormField label="Email" required>
                <input
                  type="text"
                  name="email"
                  required
                  value={venue.email}
                  onChange={(e) => setVenue({ ...venue, email: e.target.value })}
                  className="input-base"
                />
              </FormField>

              <div>
                <FormField label="Telefoon" required>
                  <input
                    type="text"
                    name="telephone"
                    required
                    value={venue.telephone}
                    onChange={(e) => setVenue({ ...venue, telephone: e.target.value })}
                    className="input-base"
                  />
                </FormField>
                {!venue.premium && (
                  <p className="text-xs text-text-tertiary mt-1.5 leading-relaxed">
                    Upgrade naar een{" "}
                    <a href="/membership" className="text-accent font-medium hover:underline">
                      Premium lidmaatschap
                    </a>{" "}
                    om het telefoonnummer te tonen.
                  </p>
                )}
                {/^(\+316|06)/.test(venue.telephone.replace(/\s/g, "")) && (
                  <label className="flex items-start gap-3 mt-3 p-3 bg-success-subtle border border-success/15 rounded-sm cursor-pointer">
                    <input
                      type="checkbox"
                      name="whatsappOptIn"
                      checked={venue.whatsappOptIn}
                      onChange={(e) => setVenue({ ...venue, whatsappOptIn: e.target.checked })}
                      className="mt-0.5"
                    />
                    <span className="text-sm text-text-primary">
                      WhatsApp-knop tonen op je locatiepagina
                      <br />
                      <small className="text-text-tertiary">
                        Bezoekers kunnen je dan direct een WhatsApp-bericht sturen. We sturen het bericht door naar {venue.telephone}.
                      </small>
                    </span>
                  </label>
                )}
              </div>

              {error && (
                <div className="mt-2">
                  <Message text={error} />
                </div>
              )}
              <div className="flex justify-end pt-2">
                <Button type="submit" label="Opslaan" pending={pending} loadingLabel="Een momentje.." />
              </div>
            </div>
          </Card>

          <Card title="Foto's">
            <div className="space-y-5">
              <Tips />

              <PhoneUploadCard venueId={params.venueId} venueName={venue.name} />

              <div>
                <span className="label-base">
                  Voeg foto's toe <span className="text-error">*</span>
                </span>
                {venue.photos.length === 0 && !venue.published && (
                  <Message variant="warning" text="Wij hebben tenminste 1 foto nodig om uw locatie op feestjegeven.nl te plaatsen." />
                )}
              </div>

              <Gallery
                objectID={params.venueId}
                images={venue.photos}
                hasPremiumAccount={venue.premium}
                onImageRemove={(id) => {
                  removedPhotosRef.current.add(id);
                  setVenue({ ...venue, photos: _.without(venue.photos, id) });
                }}
                onImageAdd={(id) => {
                  removedPhotosRef.current.delete(id);
                  setVenue({ ...venue, photos: [...venue.photos, id] });
                }}
                onSortEnd={({ active, over }) => {
                  if (over && active.id !== over.id) {
                    const oldIndex = venue.photos.indexOf(active.id);
                    const newIndex = venue.photos.indexOf(over.id);
                    setVenue({ ...venue, photos: arrayMove(venue.photos, oldIndex, newIndex) });
                  }
                }}
              />

              <p className="text-sm text-text-secondary leading-relaxed">
                <strong>LET OP:</strong> Wij hebben tenminste 1 foto van minimaal 1600x800 nodig.
                {!venue.premium && (
                  <>
                    {" "}
                    Met uw Freemium account worden alleen de eerste 2 foto's getoond. Upgrade naar een{" "}
                    <a href="/membership" className="text-accent font-medium hover:underline">
                      Premium lidmaatschap
                    </a>{" "}
                    om alle foto's te tonen.
                  </>
                )}
              </p>

              {error && <Message text={error} />}
              <div className="flex justify-end pt-2">
                <Button type="submit" label="Opslaan" pending={pending} loadingLabel="Een momentje.." />
              </div>
            </div>
          </Card>

          <Card title="Beschrijving & Capaciteit">
            <div className="space-y-5">
              <div>
                <FormField label="Beschrijving" required>
                  <textarea
                    name="description"
                    required
                    rows="8"
                    placeholder="Wat maakt deze locatie zo uniek? Waarom zou ik juist deze locatie willen boeken?"
                    onChange={(e) => setVenue({ ...venue, description: { ...venue.description, nl: e.target.value } })}
                    className="input-base resize-y"
                    value={venue.description.nl}
                  />
                </FormField>
                <DescriptionGenerator
                  venue={venue}
                  currentDescription={venue.description.nl}
                  onAccept={(text) => setVenue({ ...venue, description: { ...venue.description, nl: text } })}
                />
                <p className="text-xs text-text-tertiary mt-2 leading-relaxed">
                  Vertel iets over uw locatie, wat maakt deze locatie zo uniek? Voor wat voor gelegenheden is uw locatie te boeken?
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Minimaal aantal personen" required>
                  <input
                    type="text"
                    name="minCapacity"
                    required
                    value={venue.minCapacity}
                    onChange={(e) => setVenue({ ...venue, minCapacity: e.target.value })}
                    className="input-base"
                  />
                </FormField>
                <FormField label="Maximaal aantal personen" required>
                  <input
                    type="text"
                    name="maxCapacity"
                    required
                    value={venue.maxCapacity}
                    onChange={(e) => setVenue({ ...venue, maxCapacity: e.target.value })}
                    className="input-base"
                  />
                </FormField>
              </div>

              {error && <Message text={error} />}
              <div className="flex justify-end pt-2">
                <Button type="submit" label="Opslaan" pending={pending} loadingLabel="Een momentje.." />
              </div>
            </div>
          </Card>

          <Card title="Prijzen">
            <div className="space-y-5">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  name="exclusiveHire"
                  checked={venue.exclusiveHire}
                  onChange={(e) => setVenue({ ...venue, exclusiveHire: e.target.checked })}
                  className="accent-accent w-4 h-4"
                />
                <span className="text-sm font-medium text-text-primary">Gehele locatie is af te huren</span>
              </label>

              {venue.exclusiveHire && (
                <FormField label="Afhuurprijs (vanaf)">
                  <div className="flex">
                    <span className="inline-flex items-center px-3 border border-r-0 border-border rounded-l-sm bg-bg-sunken text-sm text-text-secondary font-medium">
                      &euro;
                    </span>
                    <input
                      type="number"
                      name="exclusiveHirePrice"
                      placeholder="bijv. 1500"
                      min="0"
                      value={venue.exclusiveHirePrice}
                      onChange={(e) => setVenue({ ...venue, exclusiveHirePrice: e.target.value })}
                      className="input-base rounded-l-none"
                    />
                  </div>
                </FormField>
              )}

              <FormField label="Prijs per persoon (vanaf)">
                <div className="flex">
                  <span className="inline-flex items-center px-3 border border-r-0 border-border rounded-l-sm bg-bg-sunken text-sm text-text-secondary font-medium">
                    &euro;
                  </span>
                  <input
                    type="number"
                    name="pricePerPerson"
                    placeholder="bijv. 35"
                    min="0"
                    value={venue.pricePerPerson}
                    onChange={(e) => setVenue({ ...venue, pricePerPerson: e.target.value })}
                    className="input-base rounded-l-none"
                  />
                </div>
              </FormField>

              {error && <Message text={error} />}
              <div className="flex justify-end pt-2">
                <Button type="submit" label="Opslaan" pending={pending} loadingLabel="Een momentje.." />
              </div>
            </div>
          </Card>

          <Card title="Gelegenheden">
            <div className="space-y-5">
              <p className="text-sm text-text-secondary -mt-1">
                Voor welke gelegenheden is uw locatie geschikt? Dit is het belangrijkste filter op de homepage.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {_.map(i18n.eventTypes, (slug, label) => (
                  <FeatureChip
                    key={`event-${slug}`}
                    label={label}
                    name={slug}
                    checked={_.includes(venue.eventTypes, slug)}
                    onChange={(e) => setVenue({ ...venue, eventTypes: _.xor(venue.eventTypes || [], [e.target.name]) })}
                  />
                ))}
              </div>

              {error && <Message text={error} />}
              <div className="flex justify-end pt-2">
                <Button type="submit" label="Opslaan" pending={pending} loadingLabel="Een momentje.." />
              </div>
            </div>
          </Card>

          <Card title="Kenmerken & Type">
            <div className="space-y-6">
              <div>
                <span className="label-base mb-3 block">Kenmerken</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {_.map(i18n.features, (feature, label) => (
                    <FeatureChip
                      key={`feature-${feature}`}
                      label={label}
                      name={feature}
                      checked={_.includes(venue.features, feature)}
                      onChange={(e) => setVenue({ ...venue, features: _.xor(venue.features, [e.target.name]) })}
                    />
                  ))}
                </div>
              </div>

              <div>
                <span className="label-base mb-3 block">Type locatie</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {_.map(i18n.types, (type, label) => (
                    <FeatureChip
                      key={`type-${type}`}
                      label={label}
                      name={type}
                      checked={_.includes(venue.types, type)}
                      onChange={(e) => setVenue({ ...venue, types: _.xor(venue.types, [e.target.name]) })}
                    />
                  ))}
                </div>
              </div>

              <div>
                <span className="label-base mb-3 block">Toegankelijkheid</span>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {_.map(i18n.accessibility, (item, label) => (
                    <FeatureChip
                      key={`access-${item}`}
                      label={label}
                      name={item}
                      checked={_.includes(venue.accessibility, item)}
                      onChange={(e) => setVenue({ ...venue, accessibility: _.xor(venue.accessibility, [e.target.name]) })}
                    />
                  ))}
                </div>
              </div>

              {error && <Message text={error} />}
              <div className="flex justify-end pt-2">
                <Button type="submit" label="Opslaan" pending={pending} loadingLabel="Een momentje.." />
              </div>
            </div>
          </Card>

          <div className="card border-error/20 bg-error-subtle p-6">
            <h3 className="text-lg font-heading font-bold text-error mb-2">Gevarenzone</h3>
            <p className="text-sm text-text-secondary mb-4">
              Met de onderstaande actie wordt deze locatie en de bijbehorende gegevens permanent van je account verwijderd.
            </p>
            {error && <Message text={error} />}
            <Button
              variant="destructive-ghost"
              label="Locatie verwijderen"
              onClick={onRemove}
              pending={pendingRemove}
              loadingLabel="Wordt verwijderd.."
              icon={Trash2}
            />
          </div>
        </form>
      </div>

      {/* Preview drawer with attached tab */}
      {venue.objectID && (
        <div
          className="hidden xl:block fixed top-0 right-0 bottom-0 z-30"
          style={{
            width: "min(520px, 40vw)",
            transform: previewOpen ? "translateX(0)" : "translateX(100%)",
            transition: "transform 300ms cubic-bezier(0.2, 0, 0, 1)",
          }}
        >
          {/* Tab handle — always visible, attached to the left edge of the panel */}
          <button
            type="button"
            onClick={() => setPreviewOpen((o) => !o)}
            className={[
              "absolute z-10 flex flex-col items-center gap-2 rounded-l-lg border border-r-0 shadow-md cursor-pointer transition-colors duration-fast",
              previewOpen
                ? "bg-accent border-accent/80 text-white"
                : "bg-bg-surface border-border text-text-secondary hover:text-text-primary hover:bg-bg-sunken",
            ].join(" ")}
            style={{ right: "100%", top: "72px", padding: "12px 7px" }}
          >
            {previewOpen ? <EyeOff size={14} /> : <Eye size={14} />}
            <span
              className="text-[10px] font-semibold tracking-widest uppercase leading-none"
              style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
            >
              Voorbeeld
            </span>
          </button>

          {/* Panel */}
          <div className="h-full flex flex-col bg-bg-surface border-l border-border shadow-xl">
            <div className="flex items-center justify-between gap-3 px-4 h-topbar border-b border-border shrink-0">
              <span className="text-sm font-medium text-text-primary truncate">
                {venue.name || "Voorbeeld"}
              </span>
              <button
                type="button"
                title="Vernieuwen"
                onClick={() => setPreviewKey((k) => k + 1)}
                className="p-1.5 rounded-sm text-text-tertiary hover:text-text-primary hover:bg-bg-sunken transition-colors duration-fast"
              >
                <RefreshCw size={13} />
              </button>
            </div>

            <iframe
              ref={iframeRef}
              key={previewKey}
              src={`https://feestjegeven.nl/l/embed/${venue.objectID}`}
              title="Locatievoorbeeld"
              className="flex-1 w-full border-0 bg-bg-base"
              loading="eager"
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default Venue;
